import {
  CBadge,
  CButton,
  CButtonGroup,
  CCard,
  CCardBody,
  CCardHeader,
  CCol,
  CDataTable,
  CRow,
} from "@coreui/react";
import { useEffect, useState } from "react";
import { BsFillPlusCircleFill } from "react-icons/bs";
import { useHistory } from "react-router-dom";
import { useSelector } from "react-redux";
import { getList, updateItem } from "../../api/generics";
import { formatToCurrency } from "../../currency/format";
import { PYG } from "../../currency/available-currencies";
import TreasuryAccount from "../../models/treasury";
import { RootState } from "../../store";
import { SUCCESS } from "../../utils/constants/tags";
import { errorAlert, successAlert, warningAlert } from "../utils/messages";
import TreasuryAccountUpdateModal from "./TreasuryAccountUpdateModal";
import TreasuryAdjustmentModal from "./TreasuryAdjustmentModal";
import TreasuryConfirmModal from "./TreasuryConfirmModal";

const TreasuryAccountCrud = () => {
  const fields = [
    { key: "name", label: "Cuenta" },
    { key: "description", label: "Descripción" },
    { key: "balance", label: "Saldo actual", _classes: ["text-right"] },
    { key: "active", label: "Estado", _classes: ["text-center"] },
    {
      key: "actions",
      label: "Acciones",
      _classes: ["text-center"],
      filter: false,
    },
  ];

  const companyId = useSelector(
    (state: RootState) => state.company.data.company.id
  );
  const history = useHistory();
  const [loading, setLoading] = useState(true);
  const [accounts, setAccounts] = useState<TreasuryAccount[]>([]);
  const [editingAccount, setEditingAccount] = useState<
    TreasuryAccount | undefined
  >(undefined);
  const [showEditModal, setShowEditModal] = useState(false);
  const [adjustingAccount, setAdjustingAccount] = useState<
    TreasuryAccount | undefined
  >(undefined);
  const [togglingAccount, setTogglingAccount] = useState<
    TreasuryAccount | undefined
  >(undefined);

  const fetchAccounts = async () => {
    setLoading(true);
    const accountsStatus = await getList<TreasuryAccount>(
      "/treasury/accounts/",
      -1,
      0,
      new Map([["company_id", companyId?.toString()]])
    );
    if (accountsStatus.status === SUCCESS && accountsStatus.data) {
      setAccounts(accountsStatus.data.items);
    } else {
      warningAlert(accountsStatus.detail ?? "Error desconocido");
    }
    setLoading(false);
  };

  const onEditSuccess = () => {
    successAlert("Datos guardados con éxito!");
    setShowEditModal(false);
    setEditingAccount(undefined);
    fetchAccounts();
  };

  const onAdjustmentSuccess = () => {
    successAlert("Ajuste registrado");
    setAdjustingAccount(undefined);
    fetchAccounts();
  };

  const onToggleActiveConfirm = async () => {
    const account = togglingAccount;
    if (account === undefined) {
      return;
    }
    const action = account.active ? "deactivate" : "activate";
    const requestStatus = await updateItem(
      `/treasury/accounts/${account.id}/${action}/`,
      {}
    );
    if (requestStatus.status !== SUCCESS) {
      errorAlert(requestStatus.detail ?? "Ha ocurrido un error!!");
      return;
    }
    successAlert(account.active ? "Cuenta dada de baja" : "Cuenta reactivada");
    setTogglingAccount(undefined);
    fetchAccounts();
  };

  useEffect(() => {
    fetchAccounts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const total = accounts
    .filter((account) => account.active)
    .reduce((sum, account) => sum + Number(account.balance ?? 0), 0);

  return (
    <>
      <CRow>
        <CCol lg="12">
          <CCard>
            <CCardHeader className="d-flex flex-row mb-3">
              <div className="p-2">
                <h3>Cuentas</h3>
              </div>
              <div className="p-2">
                <CButton
                  color="primary"
                  onClick={() => {
                    setEditingAccount(undefined);
                    setShowEditModal(true);
                  }}
                >
                  <BsFillPlusCircleFill />
                  &nbsp; Agregar Nueva
                </CButton>
              </div>
              <div className="p-2 ml-auto">
                <h4>
                  Total disponible: <b>{formatToCurrency(total, PYG)}</b>
                </h4>
              </div>
            </CCardHeader>
            <CCardBody>
              <CDataTable
                noItemsView={<h2 className="text-center">Sin Resultados</h2>}
                addTableClasses={"table-fixed"}
                fields={fields}
                items={accounts}
                striped
                border
                loading={loading}
                responsive
                scopedSlots={{
                  description: (item: TreasuryAccount) => (
                    <td>{item.description ?? "-"}</td>
                  ),
                  balance: (item: TreasuryAccount) => (
                    <td
                      className={
                        "text-right " +
                        (Number(item.balance) < 0 ? "text-danger" : "")
                      }
                    >
                      <b>{formatToCurrency(Number(item.balance ?? 0), PYG)}</b>
                    </td>
                  ),
                  active: (item: TreasuryAccount) => (
                    <td className="text-center">
                      <CBadge color={item.active ? "success" : "secondary"}>
                        {item.active ? "Activa" : "De baja"}
                      </CBadge>
                    </td>
                  ),
                  actions: (item: TreasuryAccount) => (
                    <td className="text-right">
                      <CButtonGroup>
                        <CButton
                          color="info"
                          title="Historial"
                          onClick={() =>
                            history.push(`/treasury/accounts/${item.id}/history/`)
                          }
                        >
                          <i className="fa fa-history"></i>
                        </CButton>
                        {item.active ? (
                          <CButton
                            color="success"
                            title="Ajuste manual"
                            onClick={() => setAdjustingAccount(item)}
                          >
                            <i className="fa fa-balance-scale"></i>
                          </CButton>
                        ) : (
                          <></>
                        )}
                        <CButton
                          className="text-white"
                          color="warning"
                          title="Editar"
                          onClick={() => {
                            setEditingAccount(item);
                            setShowEditModal(true);
                          }}
                        >
                          <i className="fa fa-pencil"></i>
                        </CButton>
                        <CButton
                          color={item.active ? "danger" : "secondary"}
                          title={item.active ? "Dar de baja" : "Reactivar"}
                          onClick={() => setTogglingAccount(item)}
                        >
                          <i
                            className={
                              item.active ? "fa fa-ban" : "fa fa-check"
                            }
                          ></i>
                        </CButton>
                      </CButtonGroup>
                    </td>
                  ),
                }}
              />
            </CCardBody>
          </CCard>
        </CCol>
      </CRow>
      <TreasuryAccountUpdateModal
        show={showEditModal}
        account={editingAccount}
        onCancel={() => {
          setShowEditModal(false);
          setEditingAccount(undefined);
        }}
        onSuccess={onEditSuccess}
      />
      <TreasuryConfirmModal
        show={togglingAccount !== undefined}
        title={togglingAccount?.active ? "Dar de baja" : "Reactivar"}
        color={togglingAccount?.active ? "danger" : "success"}
        confirmLabel={togglingAccount?.active ? "Dar de baja" : "Reactivar"}
        message={
          togglingAccount?.active ? (
            <>
              ¿Dar de baja la cuenta <b>{togglingAccount?.name}</b>? Deja de
              aparecer para asignar movimientos. Su historial se conserva y se
              puede reactivar.
            </>
          ) : (
            <>
              ¿Reactivar la cuenta <b>{togglingAccount?.name}</b>?
            </>
          )
        }
        onCancel={() => setTogglingAccount(undefined)}
        onConfirm={onToggleActiveConfirm}
      />
      <TreasuryAdjustmentModal
        show={adjustingAccount !== undefined}
        account={adjustingAccount}
        onCancel={() => setAdjustingAccount(undefined)}
        onSuccess={onAdjustmentSuccess}
      />
    </>
  );
};

export default TreasuryAccountCrud;
